import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    FormControl,
    FormLabel,
    Input,
    Textarea,
    VStack,
    HStack,
    Heading,
    Text,
    useToast,
    Progress,
    IconButton,
    List,
    ListItem,
    Flex,
    Badge,
    useDisclosure,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import getCSRFtoken from '../utils/getCSRFtoken';
import ResponseTemplates from './ResponseTemplates';
import fetchWithAuth from '../utils/fetchWithAuth';

function PollCreationWizard() {
    const navigate = useNavigate();
    const toast = useToast();
    const csrfToken = getCSRFtoken();
    const { isOpen: isTemplatesOpen, onOpen: onTemplatesOpen, onClose: onTemplatesClose } = useDisclosure();

    const [currentStep, setCurrentStep] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [activeQuestionIndex, setActiveQuestionIndex] = useState(null);

    // Step 1: Poll Details
    const [pollData, setPollData] = useState({
        title: '',
        description: '',
        response_goal: 100,
    });

    // Step 2: Questions & Choices
    const [questions, setQuestions] = useState([
        { text: '', choices: ['', ''] }
    ]);

    const steps = [
        { title: 'Poll Details', description: 'Basic information' },
        { title: 'Questions & Choices', description: 'Add questions and response options' },
        { title: 'Review', description: 'Review and create' },
    ];

    const progress = ((currentStep + 1) / steps.length) * 100;

    // Validation for Step 1
    const validateStep1 = () => {
        if (!pollData.title.trim()) {
            toast({
                title: 'Validation Error',
                description: 'Poll title is required.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
            return false;
        }

        if (pollData.title.trim().length < 3) {
            toast({
                title: 'Validation Error',
                description: 'Poll title must be at least 3 characters.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
            return false;
        }

        return true;
    };

    // Validation for Step 2
    const validateStep2 = () => {
        // Check if there's at least one question
        const validQuestions = questions.filter(q => q.text.trim());
        if (validQuestions.length === 0) {
            toast({
                title: 'Validation Error',
                description: 'At least one question is required.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
            return false;
        }

        // Check if each valid question has at least 2 choices
        for (let i = 0; i < validQuestions.length; i++) {
            const validChoices = validQuestions[i].choices.filter(c => c.trim());
            if (validChoices.length < 2) {
                toast({
                    title: 'Validation Error',
                    description: `Question ${i + 1} must have at least 2 choices.`,
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
                return false;
            }
        }

        return true;
    };

    const handleNext = () => {
        if (currentStep === 0 && !validateStep1()) return;
        if (currentStep === 1 && !validateStep2()) return;
        setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
    };

    const handleBack = () => {
        setCurrentStep(prev => Math.max(prev - 1, 0));
    };

    const addQuestion = () => {
        setQuestions([...questions, { text: '', choices: ['', ''] }]);
    };

    const updateQuestion = (index, text) => {
        const updated = [...questions];
        updated[index].text = text;
        setQuestions(updated);
    };

    const deleteQuestion = (index) => {
        setQuestions(questions.filter((_, i) => i !== index));
    };

    const addChoice = (questionIndex) => {
        const updated = [...questions];
        updated[questionIndex].choices.push('');
        setQuestions(updated);
    };

    const updateChoice = (questionIndex, choiceIndex, value) => {
        const updated = [...questions];
        updated[questionIndex].choices[choiceIndex] = value;
        setQuestions(updated);
    };

    const deleteChoice = (questionIndex, choiceIndex) => {
        const updated = [...questions];
        updated[questionIndex].choices = updated[questionIndex].choices.filter((_, i) => i !== choiceIndex);
        setQuestions(updated);
    };

    const applyTemplate = (choices) => {
        if (activeQuestionIndex !== null) {
            const updated = [...questions];
            updated[activeQuestionIndex].choices = choices;
            setQuestions(updated);
        }
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);

        try {
            // Step 1: Create Poll
            const pollResponse = await fetchWithAuth('/api/polls/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken,
                },
                body: JSON.stringify(pollData),
            });

            if (!pollResponse.ok) {
                throw new Error('Failed to create poll');
            }

            const poll = await pollResponse.json();

            // Step 2: Create Questions and Choices
            const validQuestions = questions.filter(q => q.text.trim());

            for (let i = 0; i < validQuestions.length; i++) {
                const question = validQuestions[i];

                const questionResponse = await fetchWithAuth('/api/questions/', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRFToken': csrfToken,
                    },
                    body: JSON.stringify({
                        text: question.text,
                        poll: poll.id,
                        order: i + 1,
                    }),
                });

                if (!questionResponse.ok) {
                    throw new Error(`Failed to create question ${i + 1}`);
                }

                const createdQuestion = await questionResponse.json();

                // Create choices for this question
                const validChoices = question.choices.filter(c => c.trim());
                for (let j = 0; j < validChoices.length; j++) {
                    const choiceResponse = await fetchWithAuth('/api/choices/', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-CSRFToken': csrfToken,
                        },
                        body: JSON.stringify({
                            text: validChoices[j],
                            question: createdQuestion.id,
                            order: j + 1,
                        }),
                    });

                    if (!choiceResponse.ok) {
                        throw new Error(`Failed to create choice ${j + 1} for question ${i + 1}`);
                    }
                }
            }

            toast({
                title: 'Poll Created!',
                description: `"${poll.title}" has been created with ${validQuestions.length} question(s).`,
                status: 'success',
                duration: 5000,
                isClosable: true,
            });

            // Navigate to the poll management page
            navigate(`/manage-questions/${poll.id}`);

        } catch (error) {
            toast({
                title: 'Error Creating Poll',
                description: error.message || 'An unexpected error occurred.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <ResponseTemplates
                isOpen={isTemplatesOpen}
                onClose={onTemplatesClose}
                onSelectTemplate={applyTemplate}
            />
            <Box maxWidth="900px" margin="0 auto" py={8}>
                <VStack spacing={6} align="stretch">
                {/* Header */}
                <Box>
                    <Heading size="lg" mb={2}>Create New Poll</Heading>
                    <Progress value={progress} size="sm" colorScheme="blue" borderRadius="full" />
                    <HStack mt={3} spacing={4}>
                        {steps.map((step, index) => (
                            <Box key={index} flex={1}>
                                <Text
                                    fontSize="sm"
                                    fontWeight={index === currentStep ? 'bold' : 'normal'}
                                    color={index === currentStep ? 'blue.600' : 'gray.500'}
                                >
                                    {index + 1}. {step.title}
                                </Text>
                            </Box>
                        ))}
                    </HStack>
                </Box>

                {/* Step Content */}
                <Box
                    p={6}
                    bg="white"
                    borderRadius="lg"
                    borderWidth="1px"
                    borderColor="gray.200"
                    minHeight="400px"
                >
                    {currentStep === 0 && (
                        <VStack spacing={4} align="stretch">
                            <Heading size="md">Poll Details</Heading>
                            <FormControl isRequired>
                                <FormLabel>Poll Title</FormLabel>
                                <Input
                                    value={pollData.title}
                                    onChange={(e) => setPollData({ ...pollData, title: e.target.value })}
                                    placeholder="Enter a descriptive title"
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel>Description</FormLabel>
                                <Textarea
                                    value={pollData.description}
                                    onChange={(e) => setPollData({ ...pollData, description: e.target.value })}
                                    placeholder="Describe the purpose of this poll"
                                    rows={4}
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel>Response Goal</FormLabel>
                                <Input
                                    type="number"
                                    value={pollData.response_goal}
                                    onChange={(e) => setPollData({ ...pollData, response_goal: parseInt(e.target.value) || 0 })}
                                    min={1}
                                />
                            </FormControl>
                        </VStack>
                    )}

                    {currentStep === 1 && (
                        <VStack spacing={4} align="stretch">
                            <HStack justify="space-between">
                                <Heading size="md">Questions & Choices</Heading>
                                <Button leftIcon={<AddIcon />} size="sm" onClick={addQuestion}>
                                    Add Question
                                </Button>
                            </HStack>

                            {questions.map((question, qIndex) => (
                                <Box
                                    key={qIndex}
                                    p={4}
                                    bg="gray.50"
                                    borderRadius="md"
                                    borderWidth="1px"
                                    borderColor="gray.200"
                                >
                                    <HStack mb={3} justify="space-between">
                                        <Badge colorScheme="blue">Question {qIndex + 1}</Badge>
                                        {questions.length > 1 && (
                                            <IconButton
                                                icon={<DeleteIcon />}
                                                size="sm"
                                                colorScheme="red"
                                                variant="ghost"
                                                onClick={() => deleteQuestion(qIndex)}
                                                aria-label="Delete question"
                                            />
                                        )}
                                    </HStack>

                                    <FormControl mb={3}>
                                        <FormLabel fontSize="sm">Question Text</FormLabel>
                                        <Input
                                            value={question.text}
                                            onChange={(e) => updateQuestion(qIndex, e.target.value)}
                                            placeholder="Enter your question"
                                            bg="white"
                                        />
                                    </FormControl>

                                    <FormControl>
                                        <HStack justify="space-between" mb={2}>
                                            <FormLabel fontSize="sm" mb={0}>Answer Choices</FormLabel>
                                            <Button
                                                size="xs"
                                                colorScheme="purple"
                                                variant="outline"
                                                onClick={() => {
                                                    setActiveQuestionIndex(qIndex);
                                                    onTemplatesOpen();
                                                }}
                                            >
                                                📋 Use Template
                                            </Button>
                                        </HStack>
                                        <VStack spacing={2} align="stretch">
                                            {question.choices.map((choice, cIndex) => (
                                                <HStack key={cIndex}>
                                                    <Input
                                                        value={choice}
                                                        onChange={(e) => updateChoice(qIndex, cIndex, e.target.value)}
                                                        placeholder={`Choice ${cIndex + 1}`}
                                                        bg="white"
                                                        size="sm"
                                                    />
                                                    {question.choices.length > 2 && (
                                                        <IconButton
                                                            icon={<DeleteIcon />}
                                                            size="sm"
                                                            colorScheme="red"
                                                            variant="ghost"
                                                            onClick={() => deleteChoice(qIndex, cIndex)}
                                                            aria-label="Delete choice"
                                                        />
                                                    )}
                                                </HStack>
                                            ))}
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                leftIcon={<AddIcon />}
                                                onClick={() => addChoice(qIndex)}
                                            >
                                                Add Choice
                                            </Button>
                                        </VStack>
                                    </FormControl>
                                </Box>
                            ))}
                        </VStack>
                    )}

                    {currentStep === 2 && (
                        <VStack spacing={6} align="stretch">
                            <Heading size="md">Review Your Poll</Heading>

                            <Box>
                                <Text fontWeight="bold" mb={2}>Poll Details</Text>
                                <Box p={4} bg="gray.50" borderRadius="md">
                                    <Text><strong>Title:</strong> {pollData.title}</Text>
                                    {pollData.description && (
                                        <Text mt={2}><strong>Description:</strong> {pollData.description}</Text>
                                    )}
                                    <Text mt={2}><strong>Response Goal:</strong> {pollData.response_goal}</Text>
                                </Box>
                            </Box>

                            <Box>
                                <Text fontWeight="bold" mb={2}>Questions ({questions.filter(q => q.text.trim()).length})</Text>
                                <VStack spacing={3} align="stretch">
                                    {questions.filter(q => q.text.trim()).map((question, index) => (
                                        <Box key={index} p={4} bg="gray.50" borderRadius="md">
                                            <Text fontWeight="semibold" mb={2}>
                                                {index + 1}. {question.text}
                                            </Text>
                                            <List spacing={1} pl={4}>
                                                {question.choices.filter(c => c.trim()).map((choice, cIndex) => (
                                                    <ListItem key={cIndex} fontSize="sm" color="gray.600">
                                                        • {choice}
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Box>
                                    ))}
                                </VStack>
                            </Box>
                        </VStack>
                    )}
                </Box>

                {/* Navigation Buttons */}
                <Flex justify="space-between">
                    <Button
                        onClick={handleBack}
                        isDisabled={currentStep === 0 || isSubmitting}
                        variant="outline"
                    >
                        Back
                    </Button>

                    {currentStep < steps.length - 1 ? (
                        <Button onClick={handleNext} colorScheme="blue">
                            Next
                        </Button>
                    ) : (
                        <Button
                            onClick={handleSubmit}
                            colorScheme="green"
                            isLoading={isSubmitting}
                            loadingText="Creating Poll..."
                        >
                            Create Poll
                        </Button>
                    )}
                </Flex>
            </VStack>
        </Box>
        </>
    );
}

export default PollCreationWizard;
